import React, { useEffect, useMemo, useRef, useState } from "react";
import FitHotspots from "./FitHotspots";
import { subscribeHotspotsShown, getHotspotsShown, setHotspotsShown, emitHotspot, subscribeIntro } from "./fitHotspotStore";
import { heroHotspots } from "configs/fitCards";
import SpinIcon from "assets/icons/spin3d.png";
import { useGrade, gradeToFilter, gradeEnabled, isGraded as gradeActive } from "utils/turntableGrade";
import { track } from "utils/jockeyTracking";

// Drag-to-spin render turntable that drops in where a 3D ModelViewer was.
// Driven purely by props (shape + size from the existing experience UI) —
// no picker chrome of its own. Frames:
//   /turntable/1820_<Shape>_<Size>_grid/frame_<angle>_<zoom>_eye.webp
const SPIN_COUNT = 18;
const SPIN_STEP = 20;
const PX_PER_FRAME = 12;

// Pre-rendered+upscaled turntable frames, hosted on S3 (kept out of the app
// image). `prefix` selects the product set: "1820" (Fragrant Lily rose) or
// "MI16" (Grape Wine sports bra) — both share the body grid (Shape_CupBand).
const TURNTABLE_CDN =
  process.env.REACT_APP_TURNTABLE_CDN ||
  "https://turntable.dopplr.info";
// Background variant per product. The activewear lines (MI16 Grape Wine, MI10
// zip-front) use the gym-composited frame set (`grid_gym`) and 1820 (Fragrant
// Lily) the hallway set (`grid_hall`); every other product uses the default
// studio (`grid_rose`).
const BG_VARIANT = { MI16: "grid_gym", MI10: "grid_gym", 1820: "grid_hall" };
// Shapes with no composited frames yet. They must fall back to the studio set:
// asking for a variant prefix that was never uploaded 404s the whole body.
const BG_VARIANT_SKIP = { 1820: ["EastWest"] };
const variantFor = (prefix, shape) =>
  (BG_VARIANT_SKIP[prefix] || []).includes(shape)
    ? "grid_rose"
    : BG_VARIANT[prefix] || "grid_rose";
const dirFor = (prefix, shape, size) =>
  `${TURNTABLE_CDN}/turntable/${prefix}_${shape}_${size}_${variantFor(prefix, shape)}`;
const frameSrc = (prefix, shape, size, angle, zoom) =>
  `${dirFor(prefix, shape, size)}/frame_${String(angle).padStart(3, "0")}_${zoom}_eye.webp`;

// Band sizing without extra renders: only 34 and 40 are rendered. Bands 30/32
// derive from the 34 render and 36/38 from the 40 render by scaling the x-axis
// only (narrower / wider torso) — matching how the 3D avatar is scaled down.
// Returns the base size to load + the horizontal scale to apply.
const bandScale = (size, product) => {
  const m = /^([A-Z]+)(\d{2})$/.exec(size || "");
  if (!m) return { base: size, scaleX: 1 };
  const cup = m[1];
  const band = parseInt(m[2], 10);
  // 301 (Jockey 1722) has its own rendered bases: 36 and 40. Bands 30/32/34 derive
  // from the 36 render, 38 from the 40 render (which is itself the 42-base body
  // baked down to a true 40 in 3D). Full unclamped band/base x-scale — this page
  // only serves bands up to 40. Other products keep the shared 34/40 bases.
  const baseBand =
    product === "301" ? (band <= 36 ? 36 : 40) : band <= 34 ? 34 : 40;
  return { base: `${cup}${baseBand}`, scaleX: band / baseBand };
};

export default function RenderTurntable({ shape, size, sizeLabel, product = "1820", onReady, heightPct = "56%", flow = false, measured = false }) {
  // Load the rendered base band (34/40) and scale x for 30/32/36/38.
  const { base: baseSize, scaleX } = useMemo(() => bandScale(size, product), [size, product]);
  // Band+cup the turntable body represents (size arrives cup-first "C34" →
  // shopper-facing "34C").
  const derivedSizeLabel = useMemo(() => {
    const m = /^([A-Z]+)(\d{2})$/.exec(size || "");
    return m ? `${m[2]}${m[1]}` : size;
  }, [size]);
  // The bubble must describe the BODY on screen. Sports bras carry an alpha
  // label (M / L / 3XL) in sizeLabel — show that. Regular bras get a band+cup
  // sizeLabel (the server clothSize), which can differ from an explicit pick;
  // show the band+cup the body actually is (derivedSizeLabel) so it matches the
  // shopper's selection rather than a re-recommendation.
  const sizeLabelIsBandCup = /^\d{2}[A-Za-z]+$/.test((sizeLabel || "").trim());
  const bubbleLabel = sizeLabelIsBandCup
    ? derivedSizeLabel || sizeLabel
    : sizeLabel || derivedSizeLabel;
  const [index, setIndex] = useState(0);
  // Push-in transform emitted by the Your Fit hotspots. When a fit answer is
  // active it holds a zoomed transform toward the feature; null the rest of the
  // time, so the frame keeps its base transform. FitHotspots applies the SAME
  // string to its overlay, keeping the pin glued to the feature through the zoom.
  const [fitZoom, setFitZoom] = useState(null);
  // Only transition the frame transform while a callout is animating in/out.
  // Leaving the transition on permanently made drag-to-rotate janky — every
  // frame swap repainted a transitioned layer — so we drop it during free spin.
  const [zoomTransit, setZoomTransit] = useState(false);
  // Hotspots ON → hide the size bubble + spin CTA and lock free-rotation (the
  // hotspots cycle owns the frame); OFF → controls show, shopper can spin.
  const [hotspotsOn, setHotspotsOn] = useState(getHotspotsShown());
  useEffect(() => subscribeHotspotsShown(setHotspotsOn), []);
  // Products that run hotspots on the MAIN turntable (MI10) supply a hero set;
  // publish it so FitHotspots has pins on the default experience (not just Your
  // Fit). null for products whose hotspots are Your-Fit-only.
  const heroSeq = useMemo(() => heroHotspots(product), [product]);
  useEffect(() => { if (heroSeq) emitHotspot(heroSeq); }, [heroSeq]);
  // A bump replays the intro lifecycle (e.g. when the shopper opens Your Fit).
  const [introNonce, setIntroNonce] = useState(0);
  useEffect(() => subscribeIntro(() => setIntroNonce((n) => n + 1)), []);
  const stageRef = useRef(null);
  const drag = useRef({ active: false, startX: 0, startIndex: 0 });

  // The panel pre-mounts this turntable OFF-SCREEN (the drawer is transform-hidden
  // until opened), so the intro rotation used to play while hidden and the shopper
  // only ever saw the finished state (features already on). Track on-screen
  // visibility and gate the intro on it, so the model rotates once → features
  // appear WHEN the panel actually opens.
  const [onScreen, setOnScreen] = useState(false);
  useEffect(() => {
    const el = stageRef.current;
    if (!el || typeof IntersectionObserver === "undefined") { setOnScreen(true); return undefined; }
    const io = new IntersectionObserver(
      (entries) => setOnScreen(entries.some((e) => e.isIntersecting)),
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // QA live image-grade, driven by the "Edit" tab in the customize bar (shared
  // store in utils/turntableGrade). Applied as an SVG filter on the frame <img>;
  // because every frame + body renders through that one element, one grade tunes
  // the whole set. Gated to QA / ?tune=1 so shoppers never see it.
  const showTuner = gradeEnabled();
  const grade = useGrade();
  const f = useMemo(() => gradeToFilter(grade), [grade]);
  const isGraded = showTuner && gradeActive();

  // Intro "rock" auto-played once on load to hint the turntable is draggable.
  const intro = useRef({ timers: [], cancelled: false });
  const cancelIntro = () => {
    intro.current.cancelled = true;
    intro.current.timers.forEach(clearTimeout);
    intro.current.timers = [];
  };

  // Preload this body's 36 frames; reset spin when shape/size changes.
  // Also un-gate the cards immediately: the experience flips sceneLoading
  // true on every shape/size change, and relying on the <img> onLoad alone
  // misses preloaded/cached frames (onLoad never fires) — which left the
  // BottomMenu cards hidden after the first interaction. Clearing it here
  // guarantees the cards come back whenever the body resolves.
  useEffect(() => {
    let cancelled = false;
    setIndex(0);
    if (onReady) onReady();
    // Frame 0 (the visible <img>, fetchpriority=high) paints first; the other
    // 17 frames are background prefetch for the intro rock + drag, so mark them
    // low priority so they never contend with the first frame on a slow first
    // load. Start at frame 1 — frame 0 is already requested by the <img>.
    for (let i = 1; i < SPIN_COUNT; i++) {
      const img = new Image();
      try { img.fetchPriority = "low"; } catch (_) {}
      img.src = frameSrc(product, shape, baseSize, i * SPIN_STEP, "close");
      if (cancelled) break;
    }
    return () => { cancelled = true; };
  }, [product, shape, baseSize]);

  const currentSrc = useMemo(
    () => frameSrc(product, shape, baseSize, index * SPIN_STEP, "close"),
    [product, shape, baseSize, index]
  );

  // Enable the transform transition when a callout zoom is set; keep it briefly
  // after it clears so the zoom-out eases, then drop it so rotation stays smooth.
  useEffect(() => {
    if (fitZoom) { setZoomTransit(true); return undefined; }
    const t = setTimeout(() => setZoomTransit(false), 650);
    return () => clearTimeout(t);
  }, [fitZoom]);

  // On first load, auto-rock the turntable so shoppers see it spins:
  // 1 → 8 → 1 → -8 → 1 (offsets from the starting frame), switching at 30fps.
  // Leaves it back on frame 1, ready to drag. A short delay lets the first
  // frames preload so the rock is smooth; any drag/spin cancels it.
  useEffect(() => {
    // Only run the intro when the turntable is actually on-screen (panel open), so
    // the rotation → features sequence is seen, not played hidden during pre-mount.
    if (!onScreen) return undefined;
    intro.current.cancelled = false; // fresh run (becomes visible, or a replay bump)
    const toIndex = (off) => ((off % SPIN_COUNT) + SPIN_COUNT) % SPIN_COUNT;
    // Hero-hotspot products: auto-play ONE full rotation on intro. Features
    // (hotspots) stay OFF by default — the shopper opts in via the Features
    // toggle. Forced OFF first so the model always rotates clean, and because
    // `hotspotsShown` is module-scoped and persists across panel opens.
    if (heroSeq) {
      setHotspotsShown(false);
      const STEP_MS = 45;
      let i = 0;
      const tick = () => {
        if (intro.current.cancelled) return;
        if (i >= SPIN_COUNT) { return; }   // rotation done — features stay OFF
        setIndex(toIndex(i));
        i += 1;
        intro.current.timers.push(setTimeout(tick, STEP_MS));
      };
      intro.current.timers.push(setTimeout(tick, 450));
      return cancelIntro;
    }
    // Other products: a small "rock" to hint the turntable is draggable.
    const OFFSETS = [
      0, 1, 2, 3, 4, 5, 6, 7, 6, 5, 4, 3, 2, 1, 0,
      -1, -2, -3, -4, -5, -6, -7, -8, -7, -6, -5, -4, -3, -2, -1, 0,
    ];
    const STEP_MS = 33; // 30fps
    let i = 0;
    const tick = () => {
      if (intro.current.cancelled || i >= OFFSETS.length) return;
      setIndex(toIndex(OFFSETS[i]));
      i += 1;
      intro.current.timers.push(setTimeout(tick, STEP_MS));
    };
    intro.current.timers.push(setTimeout(tick, 450));
    return cancelIntro;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heroSeq, introNonce, onScreen]);

  const onDown = (e) => {
    // A drag turns hotspots OFF and hands control to the shopper for free spin.
    if (hotspotsOn) setHotspotsShown(false);
    cancelIntro();
    drag.current = { active: true, startX: e.clientX, startIndex: index, frames: 0 };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onMove = (e) => {
    const d = drag.current;
    if (!d.active) return;
    const delta = Math.round((e.clientX - d.startX) / PX_PER_FRAME);
    let next = (d.startIndex - delta) % SPIN_COUNT;
    if (next < 0) next += SPIN_COUNT;
    d.frames = Math.abs(delta);
    setIndex(next);
  };
  const onUp = () => {
    const d = drag.current;
    d.active = false;
    // One engagement event per drag gesture that actually rotated the body —
    // the intro auto-rock uses setIndex directly, so it never trips this.
    if (d.frames > 0) {
      try { track("turntable_drag", { frames: d.frames, shape, size: sizeLabel || size }, product); } catch (_) {}
      d.frames = 0;
    }
  };

  // Spin CTA: advance 3 frames to the right, stepping through each intermediate
  // frame in quick succession (e.g. 1 → 2 → 3 → 4) so it reads as a short spin
  // rather than a jump. Guarded so rapid taps don't stack overlapping runs.
  const spinning = useRef(false);
  const spinForward = () => {
    if (spinning.current) return;
    cancelIntro();
    try { track("turntable_spin", { via: "button", shape, size: sizeLabel || size }, product); } catch (_) {}
    spinning.current = true;
    let steps = 0;
    const stepOnce = () => {
      setIndex((i) => (i + 1) % SPIN_COUNT);
      steps += 1;
      if (steps < 3) {
        setTimeout(stepOnce, 110);
      } else {
        spinning.current = false;
      }
    };
    stepOnce();
  };

  // The shared .ModelViewer__Container is absolute and fills the whole
  // experience height, but the cards (FloatingButtonBar) are pinned to the
  // bottom with z-index 1200. Live model-viewer framed the avatar into the
  // TOP region and left the lower area empty for the cards. We mirror that:
  // pin the stage to the top STAGE_H slice and `cover`-fill it (cropping the
  // arms) so the bust render sits cleanly ABOVE the cards, never centered into
  // them. Pointer capture stays inside this slice so the cards below stay live.
  const STAGE_H = heightPct;
  // Flow mode (dynamic-height plugin): the stage takes real space in the
  // document flow (relative + fixed aspect) so the cards can sit right below it
  // and the iframe height tracks the content. Default mode keeps the legacy
  // absolute top-slice used by the fixed-viewport experiences.
  const stageStyle = flow
    ? {
        width: "100%",
        aspectRatio: "1 / 1",
        position: "relative",
        overflow: "hidden",
        // pan-y: let vertical swipes scroll the page (the stage only rotates on
        // horizontal drag, via e.clientX). "none" here trapped every gesture,
        // so shoppers couldn't scroll past the experience on touch devices.
        touchAction: "pan-y",
        userSelect: "none",
        cursor: "grab",
      }
    : {
        width: "100%",
        height: STAGE_H,
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        overflow: "hidden",
        // pan-y: let vertical swipes scroll the page (the stage only rotates on
        // horizontal drag, via e.clientX). "none" here trapped every gesture,
        // so shoppers couldn't scroll past the experience on touch devices.
        touchAction: "pan-y",
        userSelect: "none",
        cursor: "grab",
      };
  return (
    <div
      ref={stageRef}
      className="RenderTurntable__Stage"
      style={stageStyle}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
    >
      <img
        src={currentSrc}
        alt={`${shape} ${size}`}
        draggable={false}
        fetchpriority="high"
        onLoad={() => onReady && onReady()}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "center 30%",
          pointerEvents: "none",
          // Zoom in to push the render's white background below the stage (the
          // frame is landscape; cover otherwise shows the full frame height incl.
          // the empty space under the body). scaleX additionally narrows/widens
          // the torso for non-rendered bands (30/32/36/38). Zoomed out 2 notches
          // (1.28 → 1.08) so the body reads a touch smaller / less cropped.
          // fitZoom (from FitHotspots) swaps in a cinematic push-in toward the
          // active feature; base transform keeps the same translate/scale/scaleX
          // shape so the two states animate smoothly into each other.
          // Show 10% less from below: zoom the base view in (1.08→1.20) so the
          // bottom 10% of the frame falls outside the stage, with a small +2.45%
          // downward nudge so the top edge stays covered (no gap above the body).
          // Derived to crop exactly 10% off the visible bottom while filling.
          transform: fitZoom || `translate(0%, 2.45%) scale(1.20) scaleX(${scaleX})`,
          transformOrigin: "center 22%",
          transition: zoomTransit ? "transform .6s cubic-bezier(.38,.05,.2,1)" : "none",
          filter: isGraded ? "url(#ttGrade)" : undefined,
        }}
      />
      {/* QA grade filter (0-size def) — rebuilt whenever a slider moves. */}
      {showTuner && (
        <svg aria-hidden width="0" height="0" style={{ position: "absolute", width: 0, height: 0 }}>
          <filter id="ttGrade" colorInterpolationFilters="sRGB">
            <feColorMatrix type="saturate" values={f.saturate} />
            <feComponentTransfer>
              <feFuncR type="linear" slope={f.slopeR} intercept={f.intercept} />
              <feFuncG type="linear" slope={f.slopeG} intercept={f.intercept} />
              <feFuncB type="linear" slope={f.slopeB} intercept={f.intercept} />
            </feComponentTransfer>
            {f.sharpen > 0 && (
              <feConvolveMatrix order="3" preserveAlpha="true" divisor="1" kernelMatrix={f.kernel} />
            )}
          </filter>
        </svg>
      )}
      {/* Your Fit hotspots overlay — spotlights the feature a fit answer refers
          to, tracking the current frame so it sticks as the shopper spins. Its
          own on/off toggle sits top-right. Driven by the Your Fit card via the
          fitHotspot store. */}
      <FitHotspots
        product={product}
        shape={shape}
        size={baseSize}
        angle={index * SPIN_STEP}
        setAngle={(a) => setIndex(((a / SPIN_STEP) % SPIN_COUNT + SPIN_COUNT) % SPIN_COUNT)}
        scaleX={scaleX}
        onZoom={setFitZoom}
      />
      {/* Bottom fade-to-black + tagline — an OVERLAY (adds no layout height, so
          the cards below never move). Sits under the size bubble + spin CTA
          (z-index 5), which stay on top. */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute", left: 0, right: 0, bottom: 0, height: "34%",
          // Ease back at the very bottom (0.30) instead of peaking at the seam
          // (was 0.72). The dark band still sits under the bubble/tagline (~85%),
          // but the edge that meets the pink options panel is soft — no hard line.
          background: "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.22) 55%, rgba(0,0,0,0.42) 85%, rgba(0,0,0,0.30) 100%)",
          pointerEvents: "none", zIndex: 3,
        }}
      />
      {/* Size bubble — top-LEFT corner, in line with the Features toggle
          (top-right). Stays visible whether features are on or off. */}
      {bubbleLabel && (
        <div
          style={{
            position: "absolute", top: 12, left: 12, zIndex: 8,
            background: "rgba(255,255,255,0.38)", borderRadius: 12, padding: "6px 12px",
            backdropFilter: "blur(12px) saturate(1.4)", WebkitBackdropFilter: "blur(12px) saturate(1.4)",
            border: "1px solid rgba(255,255,255,0.5)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column",
            lineHeight: 1.12, pointerEvents: "none",
          }}
        >
          <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: "0.08em", color: "#1a1a1a", textTransform: "uppercase" }}>
            {measured ? "Your size" : "Model size"}
          </span>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: "#1a1a1a" }}>{bubbleLabel}</span>
        </div>
      )}
      {/* Bottom stack — the spin CTA row sits ABOVE the tagline (flex column), so
          however the tagline wraps on a narrow phone it can never rise into the
          controls. Anchored to the stage bottom; the fade (z-index 3) is behind. */}
      <div
        style={{
          position: "absolute", left: 0, right: 0, bottom: 0, zIndex: 5,
          display: "flex", flexDirection: "column", gap: 6,
          padding: flow ? "0 12px 8px" : "0 14px 12px",
          pointerEvents: "none",
        }}
      >
        {/* controls row (spin right; size bubble now lives top-left) */}
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end", minHeight: 44 }}>
          {/* Spin CTA — advances the turntable a few frames. stopPropagation so a
              tap doesn't also start a drag-spin. */}
          <button
            type="button"
            aria-label="Spin"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={spinForward}
            style={{
              width: 44, height: 44, borderRadius: "50%", background: "rgba(255,255,255,0.38)",
              border: "1px solid rgba(255,255,255,0.5)",
              backdropFilter: "blur(12px) saturate(1.4)", WebkitBackdropFilter: "blur(12px) saturate(1.4)",
              boxShadow: "0 2px 8px rgba(0,0,0,0.18)", display: "flex", alignItems: "center",
              justifyContent: "center", cursor: "pointer", padding: 0, flex: "none",
              opacity: hotspotsOn ? 0 : 1, pointerEvents: hotspotsOn ? "none" : "auto", transition: "opacity .3s",
            }}
          >
            <img src={SpinIcon} alt="" style={{ width: 22, height: 22 }} />
          </button>
        </div>
        {/* tagline — always below the controls row */}
        <div
          style={{
            textAlign: "center", color: "rgba(255,255,255,0.92)", fontSize: 11, fontWeight: 600,
            letterSpacing: "0.01em", lineHeight: 1.3, textShadow: "0 1px 3px rgba(0,0,0,0.45)",
            fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
          }}
        >
          <span style={{ color: "#ff9ec4" }}>✦</span> 3D interactive experience that adapts to your shape &amp; size
        </div>
      </div>
    </div>
  );
}
